import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useIncident } from 'components/IAP/hooks/incidentHooks';
import {
  useFormVersions,
  useFormVersionTip,
  useRecordFormVersionSave,
} from 'components/IAP/hooks/icsFormVersionHooks';
import { FormVersioningPanel } from 'components/IAP/femaCards/FormVersioningPanel';
import { HwcgAuthorizationSection } from 'components/IncidentConfig/CreateIncident/HwcgAuthorizationSection';
import {
  createEmptyHwcgAuthorization,
  type HwcgAuthorizationState,
} from 'components/IncidentConfig/CreateIncident/hwcgAuthorization';
import {
  HWCG_COMPANY_OPTIONS,
  hwcgLocationSummary,
  parseHwcgLatLon,
  type HwcgIncidentSpecificsState,
} from 'components/IncidentConfig/CreateIncident/hwcgIncidentSpecifics';
import { SearchableSelect } from 'components/IncidentConfig/roster/SearchableSelect';
import { useHwcgContactLists } from 'hooks/hwcgContactLists/hwcgContactListHooks';
import { useUpdateV1IncidentMutation } from 'hooks/v1Incident/useUpdateV1Incident';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import { useHwcgFlag } from 'utils/featureFlags';

const INCIDENT_SPECIFICS_BASE_ID = 'INCIDENT_SPECIFICS';
/** Lookup ignores the period. The server stamps the working period on the row. */
const FORM_PERIOD = 0;

const COMPANY_OPTIONS = HWCG_COMPANY_OPTIONS.map((company) => ({
  value: company,
  label: company,
}));

function blankSpecifics(): HwcgIncidentSpecificsState {
  return {
    incidentName: '',
    companyName: '',
    callerName: '',
    callbackNumber: '',
    callerEmail: '',
    date: '',
    time: '',
    callTaker: '',
    latitude: '',
    longitude: '',
    blockNumber: '',
    waterDepthFt: '',
    wellName: '',
    drillRigName: '',
    description: '',
  };
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asRecord(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

function specificsFromFields(
  fields: Record<string, unknown> | undefined
): HwcgIncidentSpecificsState {
  const source = fields ?? {};
  return {
    incidentName: asText(source.incidentName),
    companyName: asText(source.companyName),
    callerName: asText(source.callerName),
    callbackNumber: asText(source.callbackNumber),
    callerEmail: asText(source.callerEmail),
    date: asText(source.date),
    time: asText(source.time),
    callTaker: asText(source.callTaker),
    latitude: asText(source.latitude),
    longitude: asText(source.longitude),
    blockNumber: asText(source.blockNumber),
    waterDepthFt: asText(source.waterDepthFt),
    wellName: asText(source.wellName),
    drillRigName: asText(source.drillRigName),
    description: asText(source.description),
  };
}

function authorizationFromFields(
  fields: Record<string, unknown> | undefined
): HwcgAuthorizationState {
  const raw = asRecord(fields?.authorization);
  return {
    contactId: asText(raw.contactId),
    date: asText(raw.date),
    time: asText(raw.time),
    caller: asText(raw.caller),
    description: asText(raw.description),
    confirmed: raw.confirmed === true,
  };
}

function FieldLabel({
  htmlFor,
  children,
}: {
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className="tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-wide"
    >
      {children} <span className="tw:text-destructive">*</span>
    </Label>
  );
}

export default function IncidentSpecificsPage() {
  const hwcg = useHwcgFlag();
  if (!hwcg) return null;
  return <IncidentSpecificsEditor />;
}

function IncidentSpecificsEditor() {
  const { incidentId = '' } = useParams<{ incidentId: string }>();
  const groupGuid = useAppSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  const { data: incident } = useIncident();
  const updateIncident = useUpdateV1IncidentMutation();
  const {
    data: tip,
    isLoading,
    isError,
  } = useFormVersionTip(INCIDENT_SPECIFICS_BASE_ID, FORM_PERIOD);
  const { data: versions = [] } = useFormVersions(
    INCIDENT_SPECIFICS_BASE_ID,
    tip?.formInstanceId
  );
  const recordVersion = useRecordFormVersionSave(
    INCIDENT_SPECIFICS_BASE_ID,
    FORM_PERIOD
  );
  const contactLists = useHwcgContactLists({
    enabled: true,
    groupGuid,
  });

  const [specifics, setSpecifics] =
    useState<HwcgIncidentSpecificsState>(blankSpecifics);
  const [authorization, setAuthorization] = useState<HwcgAuthorizationState>(
    createEmptyHwcgAuthorization
  );
  const [viewingId, setViewingId] = useState<string | null>(null);
  const hydratedTipId = useRef<string | null>(null);

  useEffect(() => {
    if (!tip || viewingId) return;
    if (hydratedTipId.current === tip.id) return;
    hydratedTipId.current = tip.id;
    setSpecifics(specificsFromFields(tip.payload.fields));
    setAuthorization(authorizationFromFields(tip.payload.fields));
  }, [tip, viewingId]);

  const viewed = viewingId
    ? versions.find((version) => version.id === viewingId)
    : null;
  const displayedSpecifics = viewed
    ? specificsFromFields(viewed.payload.fields)
    : specifics;
  const displayedAuthorization = viewed
    ? authorizationFromFields(viewed.payload.fields)
    : authorization;
  const readOnly = viewed != null;

  const authorizedPeople = useMemo(
    () =>
      (contactLists.data ?? []).flatMap((list) =>
        list.members.filter((member) => member.authorizedIndividual === true)
      ),
    [contactLists.data]
  );

  const patch = (partial: Partial<HwcgIncidentSpecificsState>) => {
    if (readOnly) return;
    setSpecifics((current) => ({ ...current, ...partial }));
  };

  const save = async () => {
    if (!incident || readOnly) return;
    const point = parseHwcgLatLon(specifics.latitude, specifics.longitude);
    const settings = incident.settings ?? {};
    const hwcgSettings =
      settings.hwcg && typeof settings.hwcg === 'object'
        ? (settings.hwcg as Record<string, unknown>)
        : {};
    await updateIncident.mutateAsync({
      v1IncidentId: incident.id,
      name: specifics.incidentName.trim() || incident.name,
      incidentTypes: incident.incident_types,
      location: point ? { type: 'Point', coordinates: point } : null,
      address: point ? hwcgLocationSummary(point) : null,
      settings: {
        ...settings,
        hwcg: {
          ...hwcgSettings,
          specifics,
          authorization,
          responsibleParty: specifics.companyName,
        },
      },
    });
    await recordVersion.mutateAsync();
    await queryClient.invalidateQueries({
      queryKey: ['hwcg-group-actions', incidentId],
    });
  };

  const textField = (
    key: keyof HwcgIncidentSpecificsState,
    id: string,
    label: string,
    options: { placeholder?: string; type?: string } = {}
  ) => (
    <div className="tw:grid tw:gap-1.5">
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input
        id={id}
        type={options.type}
        placeholder={options.placeholder}
        value={displayedSpecifics[key]}
        disabled={readOnly}
        onChange={(event) => patch({ [key]: event.target.value })}
      />
    </div>
  );

  return (
    <div className="maincontent dark tw:text-foreground">
      <div className="tw:mx-auto tw:flex tw:max-w-6xl tw:flex-col tw:gap-4 tw:p-4">
        <div className="tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-3">
          <div>
            <h1 className="tw:m-0 tw:text-lg tw:font-semibold">
              Incident Specifics
            </h1>
            <p className="tw:mt-1 tw:text-xs tw:text-muted-foreground">
              The intake captured when the incident was opened. Saving records a
              new version of this form.
            </p>
          </div>
          <Button
            type="button"
            disabled={
              readOnly || updateIncident.isPending || recordVersion.isPending
            }
            onClick={() => void save()}
          >
            Save
          </Button>
        </div>

        <FormVersioningPanel
          baseId={INCIDENT_SPECIFICS_BASE_ID}
          periodNumber={FORM_PERIOD}
          onViewVersionChange={setViewingId}
        />

        {isLoading ? (
          <p className="tw:text-sm tw:text-muted-foreground">
            Loading Incident Specifics…
          </p>
        ) : null}
        {isError ? (
          <p className="tw:text-sm tw:text-destructive">
            Incident Specifics could not be loaded.
          </p>
        ) : null}

        {tip ? (
          <div className="tw:space-y-6">
            <div className="tw:grid tw:gap-4 tw:sm:grid-cols-2">
              <div className="tw:grid tw:gap-1.5 tw:sm:col-span-2">
                <FieldLabel htmlFor="incident-specifics-name">
                  Incident Name
                </FieldLabel>
                <Input
                  id="incident-specifics-name"
                  value={displayedSpecifics.incidentName}
                  disabled={readOnly}
                  onChange={(event) =>
                    patch({ incidentName: event.target.value })
                  }
                  className="tw:h-11 tw:text-base"
                />
              </div>
              <div className="tw:grid tw:gap-1.5 tw:sm:col-span-2">
                <FieldLabel htmlFor="incident-specifics-company">
                  Responsible Party
                </FieldLabel>
                <SearchableSelect
                  id="incident-specifics-company"
                  aria-label="Responsible Party"
                  options={COMPANY_OPTIONS}
                  disabled={readOnly}
                  value={
                    displayedSpecifics.companyName
                      ? {
                          value: displayedSpecifics.companyName,
                          label: displayedSpecifics.companyName,
                        }
                      : null
                  }
                  placeholder="Select responsible party"
                  contentClassName="dark"
                  positionerClassName="tw:z-[60]"
                  onChange={(next) => patch({ companyName: next?.value ?? '' })}
                />
              </div>
            </div>

            <section className="tw:space-y-3">
              <h2 className="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
                1A · Caller &amp; Notification
              </h2>
              <div className="tw:grid tw:gap-3 tw:sm:grid-cols-2 tw:lg:grid-cols-3">
                {textField(
                  'callerName',
                  'incident-specifics-caller',
                  'Caller Name',
                  { placeholder: 'First and last name' }
                )}
                {textField(
                  'callbackNumber',
                  'incident-specifics-callback',
                  'Callback #',
                  { placeholder: '(___) ___-____' }
                )}
                {textField(
                  'callerEmail',
                  'incident-specifics-email',
                  'Caller Email',
                  { type: 'email' }
                )}
                {textField('date', 'incident-specifics-date', 'Date')}
                {textField('time', 'incident-specifics-time', 'Time')}
                {textField(
                  'callTaker',
                  'incident-specifics-call-taker',
                  'HWCG Call Taker',
                  { placeholder: 'Name of HWCG call taker' }
                )}
              </div>
            </section>

            <section className="tw:space-y-3">
              <h2 className="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
                1B · Location &amp; Well
              </h2>
              <div className="tw:grid tw:gap-3 tw:sm:grid-cols-2 tw:lg:grid-cols-3">
                {textField('latitude', 'incident-specifics-lat', 'Latitude')}
                {textField('longitude', 'incident-specifics-lon', 'Longitude')}
                {textField(
                  'blockNumber',
                  'incident-specifics-block',
                  'Block + #'
                )}
                {textField(
                  'waterDepthFt',
                  'incident-specifics-depth',
                  'Water Depth (ft)'
                )}
                {textField(
                  'wellName',
                  'incident-specifics-well',
                  'Well Name and #'
                )}
                {textField(
                  'drillRigName',
                  'incident-specifics-rig',
                  'Drill Rig Name',
                  { placeholder: 'Rig name' }
                )}
              </div>
              <div className="tw:grid tw:gap-1.5">
                <FieldLabel htmlFor="incident-specifics-description">
                  Incident Description
                </FieldLabel>
                <Textarea
                  id="incident-specifics-description"
                  rows={5}
                  disabled={readOnly}
                  placeholder="Nature of the event, current well status, actions taken to date..."
                  value={displayedSpecifics.description}
                  onChange={(event) =>
                    patch({ description: event.target.value })
                  }
                />
              </div>
            </section>

            <HwcgAuthorizationSection
              companyName={displayedSpecifics.companyName}
              specificsDate={displayedSpecifics.date}
              specificsTime={displayedSpecifics.time}
              people={authorizedPeople}
              authorization={displayedAuthorization}
              readOnly={readOnly}
              onChange={(next) => {
                if (readOnly) return;
                setAuthorization(next);
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
